import { useEffect, useId, useRef, type ButtonHTMLAttributes, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";

export type ActionTone = "orange" | "blue" | "violet" | "jade" | "danger" | "ghost";
export type ActionSize = "sm" | "md" | "lg";

export interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function GlassPanel({ children, className = "", ...props }: GlassPanelProps) {
  return <div className={`glass-panel ${className}`.trim()} {...props}>{children}</div>;
}

export interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ActionTone;
  size?: ActionSize;
  wide?: boolean;
}

export function ActionButton({ tone = "jade", size = "md", wide = false, className = "", type = "button", ...props }: ActionButtonProps) {
  const classes = ["ui-button", `ui-button--${tone}`, size !== "md" ? `ui-button--${size}` : "", wide ? "ui-button--wide" : "", className].filter(Boolean).join(" ");
  return <button className={classes} type={type} {...props} />;
}

export function UtilityButton({ className = "", type = "button", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`utility-button ${className}`.trim()} type={type} {...props} />;
}

export interface SegmentOption<T extends string> { value: T; label: string; }

export function SegmentedControl<T extends string>({ label, options, value, onChange }: { label: string; options: readonly SegmentOption<T>[]; value: T; onChange: (value: T) => void }) {
  return <div className="segmented-control" role="group" aria-label={label}>
    {options.map((option) => <button key={option.value} type="button" aria-pressed={value === option.value} onClick={() => onChange(option.value)}>{option.label}</button>)}
  </div>;
}

export function ModalSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const dialogRef = useRef<HTMLElement>(null);
  const titleId = useId();
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusableSelector = "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])";
    const focusable = () => [...(dialog?.querySelectorAll<HTMLElement>(focusableSelector) ?? [])].filter((element) => element.getClientRects().length > 0);
    focusable()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); return; }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); previousFocus?.focus(); };
  }, [onClose]);
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section ref={dialogRef} className="glass-panel modal-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header className="modal-sheet__head"><h2 id={titleId}>{title}</h2><UtilityButton aria-label={`关闭${title}`} onClick={onClose}>关闭</UtilityButton></header>
      {children}
    </section>
  </div>;
}

export interface DataColumn<Row> { key: string; title: string; render: (row: Row) => ReactNode; }

export function DataTable<Row>({ label, columns, rows, getKey }: { label: string; columns: readonly DataColumn<Row>[]; rows: readonly Row[]; getKey: (row: Row) => string }) {
  return <div className="data-table-wrap"><table className="data-table" aria-label={label}><thead><tr>{columns.map((column) => <th key={column.key} scope="col">{column.title}</th>)}</tr></thead><tbody>{rows.map((row) => <tr key={getKey(row)}>{columns.map((column) => <td key={column.key}>{column.render(row)}</td>)}</tr>)}</tbody></table></div>;
}

export function Toast({ children }: { children: ReactNode }) {
  return <div className="toast" role="status" aria-live="polite">{children}</div>;
}

export interface PlayingCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  rank?: string;
  suit?: string;
  red?: boolean;
  selected?: boolean;
  locked?: boolean;
  faceDown?: boolean;
  cardBackUrl?: string;
}

export function PlayingCard({ rank = "", suit = "", red = false, selected = false, locked = false, faceDown = false, cardBackUrl, className = "", style, type = "button", ...props }: PlayingCardProps) {
  const cardStyle = { ...style, ...(cardBackUrl ? { "--card-back-image": `url(${cardBackUrl})` } : {}) } as CSSProperties;
  return <button type={type} className={["playing-card", red ? "playing-card--red" : "", selected ? "playing-card--selected" : "", locked ? "playing-card--locked" : "", faceDown ? "playing-card--back" : "", className].filter(Boolean).join(" ")} style={cardStyle} aria-pressed={selected} {...props}>
    {faceDown ? <span className="playing-card__back-mark" aria-hidden="true">同心</span> : <><span className="playing-card__rank">{rank}</span><span className="playing-card__suit">{suit}</span>{locked ? <span className="playing-card__lock" aria-label="已锁牌">锁</span> : null}</>}
  </button>;
}
